/**
 * HypICC 챔버의 **치수 비율** — 한 곳.
 *
 * 🔴 **실측이 아니다.** CTCELLS · DGIST 공개 도면(ACS AMI 2023 표지 논문 Figure 1)의
 *    **단면도 픽셀을 재서 얻은 비율**이다. 근거·측정값은 `docs/figures/hypicc/README.md`.
 *    치수를 지어내지 마라 — 실 CAD 가 오면 이 표만 갈아끼운다.
 *
 * 단위는 **챔버 바깥 폭 = 1**. 원점은 바닥 가운데(y = 0 이 바닥면).
 *
 * ```
 *        ┌───────────────────────────────┐  ← 윗면(림)          y = OUTER.h
 *        │ ┌───────────────────────────┐ │
 *   벽    │ │        우물(열림)          │ │  WELL_H
 *  WALL  │ │                           │ │
 *        │ └─▄─▄─▄─▄─▄─▄─▄─▄─▄─▄─▄─▄─┘ │  ← 미세 기둥 격자
 *        ├───────────────────────────────┤  FLOOR_H              y = 0
 * ```
 */

/** 바깥 상자 — 단면도에서 읽은 가로:세로 = 210 : 94 (= 0.448). 평면은 정사각에 가깝다 */
export const OUTER = { w: 1, d: 1, h: 0.448 } as const

/** 옆벽 두께 — 단면도 15 / 210 */
export const WALL = 0.071

/** 바닥 두께 — 단면도 24 / 94 × 0.448 */
export const FLOOR_H = 0.115

/** 우물 깊이 — 바깥 높이에서 바닥을 뺀 것 */
export const WELL_H = OUTER.h - FLOOR_H

/** 우물 안쪽 폭·깊이 */
export const WELL_W = OUTER.w - WALL * 2
export const WELL_D = OUTER.d - WALL * 2

/**
 * 바닥의 **미세 기둥 격자** — 단면도에서 기둥 폭 11px · 피치 21.7px(폭의 0.103).
 * ⚠ 실물의 기둥 위 하이드로젤 막은 **100 µm** 다(논문 Figure 1b). 우리 그림 단위로는
 *   0.0005 도 안 되어 **보이지 않는다** — 그래서 젤은 「막」이 아니라 **채움 높이**로 그린다.
 */
export const PILLAR = { pitch: 0.103, size: 0.052, h: 0.022 } as const

/**
 * 젤 안정기(gel stabilizer) — 우물에 꽂히는 **짙은 회색 틀**.
 * 단면도: 다리 두께 10/210, 림 위로 28/210 만큼 솟고 그 위에 챔버 밖으로 걸치는 턱이 있다.
 */
export const STABILIZER = {
  wall: 0.048,
  /**
   * 림 위로 솟는 높이. 단면도에서는 28/210(=0.133)인데 **0.055 로 낮췄다** —
   * 72×50 자리에서 그대로 쓰면 회색 틀이 우물 입구를 덮어 「회색 상자」로만 읽힌다
   * (2026-09-23 헤드리스 렌더로 확인). 🔴 실측 비율을 바꾼 유일한 칸이고, 까닭이 이것이다.
   */
  rise: 0.055,
  /** 걸침턱이 챔버 바깥으로 나오는 양 */
  overhang: 0.012,
  /** 걸침턱 두께 */
  lip: 0.028,
} as const

/**
 * 두께 조절기(thickness controller) — 안정기 안에 얹히는 **연한 베이지 U 틀**.
 * 분해도에서 안정기 개구부보다 조금 작고, 위에 손잡이 가로대가 있다.
 */
export const CONTROLLER = {
  /** 바깥 폭 — 안정기 개구부(0.762)보다 조금 작다 */
  w: 0.7,
  wall: 0.05,
  h: 0.14,
  /** 손잡이 가로대 */
  handle: { h: 0.055, d: 0.22 },
} as const

/**
 * **그림 자리의 가로세로비** — 2D 챔버(`ChamberFigure`)의 `viewBox` 와 같은 값이어야 한다.
 * 카드의 챔버 자리(`--chamber-w`)가 이 비율로 잡혀 있어 3D 로 갈아도 자리가 안 흔들린다.
 */
export { TRAY_CELL_H as VIEW_H, TRAY_CELL_W as VIEW_W } from '../TrayFigure.tsx'

/** 3차원 한 점 — 도형을 놓는 자리 */
export type Point3 = readonly [number, number, number]

/**
 * 우물 바닥의 **미세 기둥 격자** 자리. 🔴 **순수 함수다**(시험된다).
 * 실물은 수백 개지만 72×50 자리에서는 안 보인다 — 「바닥에 결이 있다」가 읽힐 만큼만 놓는다.
 */
export function pillarGrid(): Point3[] {
  const out: Point3[] = []
  const span = WELL_W - PILLAR.size
  const n = Math.max(2, Math.round(span / PILLAR.pitch))
  const step = span / n
  const y = FLOOR_H + PILLAR.h / 2
  for (let i = 0; i <= n; i++) {
    for (let k = 0; k <= n; k++) {
      out.push([-span / 2 + step * i, y, -span / 2 + step * k])
    }
  }
  return out
}

/**
 * 바닥 **젤 층**(PEGDA)의 높이 — 우물 깊이에 대한 비율. 등록 때 「얕게」 깔린 그 층이고, 원심분리기가
 * 세포를 **이 층 위로** 가라앉힌다(eric 2026-09-28 「원심력을 받아서 젤 부분에 분리되는」).
 */
export const BED_RATIO = 0.25

/** 젤 층의 윗면 높이 — 세포가 앉는 자리 */
export const BED_TOP = FLOOR_H + WELL_H * BED_RATIO

/**
 * 세포 덩어리 — **단순한 형태 소수**(eric 2026-09-28 「암세포도 여러 개로 표현했는데.. 그것도 심플하게」).
 * 종전 점 9 개를 **납작한 덩어리 셋**으로 줄였다. 읽혀야 하는 것은 「세포가 어디에 있나」(떠 있음 → 젤 층에
 * 앉음) 하나다. `r` 은 가로 반지름, `flat` 은 세로 비율(앉은 세포처럼 눌린 모양).
 */
export const CELL_BLOB = { count: 3, r: 0.075, flat: 0.55 } as const

/**
 * 세포 덩어리 자리. 🔴 **순수 함수다**(시험된다).
 * 🔴 **난수를 쓰지 않는다** — 다시 그릴 때마다 덩어리가 튀면 「값이 바뀐 것」으로 읽힌다.
 * `floating` 원심 «전»(부유액 속 제각각 높이) · `settled` 원심 «뒤»(젤 층 위에 한 줄로 앉음).
 */
export function cellBlobs(kind: 'settled' | 'floating' = 'settled'): Point3[] {
  const reach = WELL_W / 2 - CELL_BLOB.r * 2
  const seat = BED_TOP + CELL_BLOB.r * CELL_BLOB.flat
  /* 가로 셋 · 깊이는 살짝 어긋나게(한 줄로만 서면 판처럼 보인다) — 늘 같은 자리 */
  const xs = [-0.55, 0.05, 0.6]
  const zs = [0.25, -0.2, 0.1]
  const lift = [0.55, 0.3, 0.75]
  return xs.map((x, i): Point3 => {
    const y = kind === 'settled' ? seat : seat + (OUTER.h - seat) * (lift[i] ?? 0.5) * 0.8
    return [x * reach, y, (zs[i] ?? 0) * reach]
  })
}
